import React, { ReactNode, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

import IconGridDots from '@/assets/icons/icon-data-grid.svg';
import IconListDots from '@/assets/icons/icon-data-list.svg';
import MobileActiveIcon from '@/assets/icons/icon-mobile-active.svg';
import MobileBorrowIcon from '@/assets/icons/icon-mobile-borrow.svg';
import MobileCollectionsIcon from '@/assets/icons/icon-mobile-collections.svg';
import MobileLendIcon from '@/assets/icons/icon-mobile-lend.svg';
import MobileMyLoansIcon from '@/assets/icons/icon-mobile-my-loans.svg';
import BoxIcon from '@/assets/icons/icon-nft.svg';
import RemoveIcon from '@/assets/icons/icon-remove.svg';
import SearchIcon from '@/assets/icons/icon-search.svg';
import LoansImage from '@/assets/images/loans-signed-out.png';
import LoggedOutModal from '@/components/shared/logged-out-modal';
import CustomDropdown from '@/components/ui/CustomDropdown';
import type { DropdownItemType } from '@/components/ui/CustomDropdown';
import { useLoanTokens } from '@/hooks/query/loans/useLoanTokens';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useAuthenticate from '@/hooks/useAuthenticate';
import useFilterCurrency from '@/hooks/useFilterCurrency';
import useSearchHandler from '@/hooks/useSearchHandler';
import { changeCardView, toogleQuickLoanModal } from '@/redux/loans/loansSlice';
import { cn, sortCurrencySymbols } from '@/utils/helper';

import MobileCommingSoon from './MobileCommingSoon';

interface IBorriwLendLayout {
  children: ReactNode;
  title: string;
  description: string;
  collectionHide?: boolean;
  hideSearchBar?: boolean;
  customSearchKey?: string;
  canClose?: boolean;
  showQuickLoanButton?: boolean;
  extraDropdowns?: string[];
  showPoolOffersSwitcher?: boolean;
  hideCurrency?: boolean;
}

const loansBtns = [
  {
    type: 'active',
    text: 'Active',
    MobileIcon: MobileActiveIcon,
    description: 'Explore the latest active loans.',
  },
  {
    type: 'borrow',
    text: 'Borrow',
    MobileIcon: MobileBorrowIcon,
    description: 'Borrow against your NFTS',
  },
  {
    type: 'lend',
    text: 'Lend',
    MobileIcon: MobileLendIcon,
    description: 'View & lend to open loan requests',
  },
  {
    type: 'loan-offers',
    text: 'Loan Offers',
    MobileIcon: MobileCollectionsIcon,
    description: 'View and make collection offers',
  },
  // {
  //   type: 'my-loans',
  //   text: 'My Loans',
  //   MobileIcon: MobileMyLoansIcon,
  //   description: 'Explore and manage your loans',
  // },
];

const NavigationTabs: React.FC<{ className?: string }> = ({ className }) => {
  const router = useRouter();
  const pathname = usePathname();

  return (
    <div className={cn('hidden mb-6 min-h-[72px] lg:flex gap-4 overflow-hidden px-6 !bg-white dark:!bg-jet-black', className)}>
      {loansBtns.map(({ text, type, description: boxDescription }) => {
        const isActiveTab = pathname.includes(`/loans/${type}`);

        return (
          <button onClick={() => router.push(`/loans/${type}`)} key={text} className={cn('lg:flex-1 h-[72px] border flex flex-col gap-1 max-w-[206px] px-5 justify-center rounded', isActiveTab ? '!bg-primary-blue-400/15 border-active-border' : '!text-coal dark:!text-white lg:rounded bg-white-100 dark:bg-jet-black-100')}>
            <div className='flex items-center gap-1.5 '>
              <BoxIcon className={cn(isActiveTab ? 'dark:[&_path]:!fill-primary-blue-100 [&_path]:!fill-primary' : '[&_path]:!fill-coal-250 dark:[&_path]:!fill-white')} />
              <span className={cn(isActiveTab && 'text-primary dark:!text-primary-blue-100', 'font-medium text-base')}>{text}</span>
            </div>
            <span className='text-[10px] text-left w-max'>{boxDescription}</span>
          </button>
        );
      })}
    </div>
  );
};

const BorrowLendLayout: React.FC<IBorriwLendLayout> = ({ children, collectionHide, hideSearchBar, customSearchKey, canClose, showQuickLoanButton, extraDropdowns, showPoolOffersSwitcher, hideCurrency = false }) => {
  const { isAuth } = useAuthenticate();
  const pathname = usePathname();
  const { handleFilterCurrency } = useFilterCurrency('currency');
  const dispatch = useAppDispatch();
  const { loanCardView } = useAppSelector((state) => state.loans);
  const [filterCollection, setFilterCollection] = useState([]);
  const { handleSearch, searchValue } = useSearchHandler({ customSearchKey });
  const tokens = useLoanTokens();
  const searchParams = useSearchParams();
  const currencyValue = searchParams.get('currency')?.toString();

  const handleChangeFilter = (collectionType: string) => setFilterCollection((prev) => prev.filter(({ type }) => type !== collectionType));

  const handleChangeView = (view: string) => dispatch(changeCardView(view));

  const getModalText = () => {
    const modalTextMap: { [key in '/loans/my-loans' | '/loans/borrow']: string } = {
      '/loans/my-loans': 'Login to view your loans.',
      '/loans/borrow': 'Create an account to borrow.',
    };

    const matchedPath = Object.keys(modalTextMap).find((path) => pathname.includes(path)) as '/loans/my-loans' | '/loans/borrow' | undefined;

    return matchedPath ? modalTextMap[matchedPath] : 'Create an account to lend.';
  };

  if (!canClose && !isAuth) {
    return (
      <>
        <NavigationTabs className='py-8 !mb-0 bg-jet-black max-lg:hidden' />
        <LoggedOutModal className='max-lg:hidden' hideTitle image={LoansImage} text={getModalText()} />
        <MobileCommingSoon />
      </>
    );
  }

  const allInitialTokenData = {
    label: 'ALL',
    onClick: () => handleFilterCurrency(),
  };

  return (
    <>
      <div className='flex-grow hidden lg:flex relative dark:bg-jet-black h-full bg-white'>
        <div className='flex flex-col lg:pt-8 flex-1 h-[calc(100vh-160px)]'>
          <NavigationTabs />
          {!collectionHide && (
            <div className={cn(!hideSearchBar && 'lg:mb-4 px-6 min-h-[54px]')}>
              {!hideSearchBar && (
                <div className='hidden lg:flex items-center gap-4'>
                  <div className='relative w-full lg:w-max'>
                    <input defaultValue={searchValue} onChange={handleSearch} className='w-full placeholder:text-coal-250 peer focus:!bg-primary h-[52px] font-normal text-coal-250 dark:text-white text-sm py-4 px-12 rounded-sm lg:max-w-[345px] hover:!bg-light-blue-grey dark:hover:!bg-jet-black-100 border' placeholder='Search Collections' />
                    <SearchIcon className='absolute peer-focus:[&_path]:fill-white top-4 left-4 dark:[&_path]:fill-white size-[18px] [&_path]:fill-coal-250' />
                  </div>
                  <div className='hidden lg:flex divide-x border rounded-sm'>
                    <button onClick={() => handleChangeView('card')} className='size-[50px] flex items-center justify-center !bg-light-blue-grey dark:!bg-jet-black-100'>
                      <IconGridDots className={cn(loanCardView === 'card' ? '[&_path]:!fill-primary' : '')} />
                    </button>
                    <button onClick={() => handleChangeView('list')} className='size-[50px] flex items-center justify-center !bg-light-blue-grey dark:!bg-jet-black-100'>
                      <IconListDots className={cn(loanCardView === 'card' ? '[&_path]:fill-coal-250' : '[&_path]:!fill-primary')} />
                    </button>
                  </div>
                  {!hideCurrency &&
                    extraDropdowns?.length &&
                    extraDropdowns.map((val, idx) => {
                      const sortedTokens = sortCurrencySymbols(tokens.data || []);

                      const availableDropdowns: Record<'Currency' | 'Asset', DropdownItemType[]> = {
                        Currency: tokens.data
                          ? [
                              allInitialTokenData,
                              ...sortedTokens.map((token) => {
                                return {
                                  label: token.symbol,
                                  value: token.address,
                                  onClick: () => handleFilterCurrency(token.address),
                                };
                              }),
                            ]
                          : [allInitialTokenData],
                        Asset: [
                          {
                            label: 'NFTs',
                            // onClick: () => console.log('HELLO-1'),
                          },
                          {
                            label: 'Other',
                            // onClick: () => console.log('HELLO-2'),
                          },
                        ],
                      };

                      return <CustomDropdown key={idx + (currencyValue || '')} defaultValue={currencyValue ? { value: currencyValue, label: tokens.data?.find(({ address }) => address === currencyValue)?.symbol || '' } : null} label={val} items={availableDropdowns[val as keyof typeof availableDropdowns]} />;
                    })}
                  {showQuickLoanButton && (
                    <button onClick={() => dispatch(toogleQuickLoanModal())} className='text-white rounded h-[50px] w-[186px] flex items-center justify-center bg-primary text-sm font-medium' type='button'>
                      Quick Loan Request
                    </button>
                  )}
                </div>
              )}
              {!!filterCollection.length && (
                <div className='flex gap-[6px] min-h-[25px] px-6'>
                  {filterCollection.map(({ type, text }) => {
                    return (
                      <button onClick={() => handleChangeFilter(type)} key={type} className='!bg-light-purple dark:!bg-coal rounded-sm dark:!text-white !text-primary text-xs/[10px] font-medium px-[7px] flex items-center gap-[7px] h-6'>
                        {text}
                        <RemoveIcon className='[&_path]:!fill-primary' />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className={cn('overflow-auto custom-scrollbar flex-grow', pathname.includes('my-loans') ? 'pb-0' : '')}>{children}</div>
        </div>
      </div>
      <MobileCommingSoon />
    </>
  );
};

export default BorrowLendLayout;
